import {
  HEALTH_CARD_PHOTO_MEDIA_TYPE,
  type HealthCardPhoto,
  type HealthCardPhotoDeleteReceipt,
  HealthCardPhotoDeleteReceiptSchema,
  type HealthCardPhotoList,
  HealthCardPhotoListSchema,
  HealthCardPhotoSchema,
  type HealthCardPhotoSettings,
  HealthCardPhotoSettingsSchema,
  inspectHealthCardJpeg,
  type SaveHealthCardPhotoSettingsRequest,
  SaveHealthCardPhotoSettingsRequestSchema,
} from "@emr/contracts";
import { z } from "zod";
import { SchedulingRequestError, schedulingRequestError } from "../demo/schedulingErrors";

/**
 * Health card photos (PA-13 / EXECDEV-114) on the staff scheduling lane: a photo of the front of
 * the patient's card, sent from the patient's questionnaire link or taken by staff. Capture only:
 * nothing reads the card, and the typed number stays the record. Every route acts on the client's
 * own site (its `x-site-id`). Errors are SchedulingRequestError with a `detail`
 * (`health_card_photos_unavailable`, `health_card_photo_invalid`, `health_card_photo_not_ready`,
 * `health_card_photo_settings_changed`).
 *
 * - GET  /v1/scheduling/health-card-photo-settings              retention and whether photos can
 *                                                              be sent now (capture)
 * - POST /v1/scheduling/health-card-photo-settings              save retention (clinic_admin)
 * - GET  /v1/patients/:patientId/health-card-photos             the chart's photos (metadata)
 * - POST /v1/patients/:patientId/health-card-photos             a staff photo, raw image/jpeg
 * - GET  /v1/scheduling/appointments/:id/health-card-photos     a booking's photos (metadata)
 * - GET  /v1/scheduling/health-card-photos/:photoId/content     the bytes: clean photos only,
 *                                                              audited on every read
 * - POST /v1/scheduling/health-card-photos/:photoId/delete      delete a photo
 *
 * The lists are metadata only and not audited. The content read is audited, so callers read it
 * when a photo is put on screen, never on a poll, and drop the bytes when it leaves the screen.
 */
export function createHealthCardPhotosClient(
  request: (path: string, init: RequestInit) => Promise<Response>,
) {
  const uuid = (value: string) => encodeURIComponent(z.string().uuid().parse(value));
  async function send(
    path: string,
    fallbackMessage: string,
    init: RequestInit,
    signal: AbortSignal | undefined,
  ): Promise<Response> {
    const response = await request(path, signal === undefined ? init : { ...init, signal });
    if (!response.ok) throw await schedulingRequestError(response, fallbackMessage);
    return response;
  }
  async function json<T extends z.ZodType>(
    path: string,
    schema: T,
    fallbackMessage: string,
    body: unknown,
    signal: AbortSignal | undefined,
  ): Promise<z.output<T>> {
    const response = await send(
      path,
      fallbackMessage,
      body === undefined
        ? { method: "GET" }
        : {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          },
      signal,
    );
    return schema.parse(await response.json());
  }

  return {
    /** The clinic's retention and whether photos can be sent now (a photo checker is running). */
    async getHealthCardPhotoSettings(signal?: AbortSignal): Promise<HealthCardPhotoSettings> {
      return json(
        "/v1/scheduling/health-card-photo-settings",
        HealthCardPhotoSettingsSchema,
        "The health card photo settings could not be loaded.",
        undefined,
        signal,
      );
    },

    /** Saves the retention against `expectedVersion` (clinic administrators). */
    async saveHealthCardPhotoSettings(
      input: SaveHealthCardPhotoSettingsRequest,
      signal?: AbortSignal,
    ): Promise<HealthCardPhotoSettings> {
      return json(
        "/v1/scheduling/health-card-photo-settings",
        HealthCardPhotoSettingsSchema,
        "The health card photo settings could not be saved.",
        SaveHealthCardPhotoSettingsRequestSchema.parse(input),
        signal,
      );
    },

    /** The chart's current photos, newest first: staff photos and ones sent from its bookings. */
    async listPatientHealthCardPhotos(
      patientId: string,
      signal?: AbortSignal,
    ): Promise<HealthCardPhotoList> {
      return json(
        `/v1/patients/${uuid(patientId)}/health-card-photos`,
        HealthCardPhotoListSchema,
        "The health card photos could not be loaded.",
        undefined,
        signal,
      );
    },

    /** The photos sent from a booking's or walk-in's questionnaire link, newest first. */
    async listAppointmentHealthCardPhotos(
      appointmentId: string,
      signal?: AbortSignal,
    ): Promise<HealthCardPhotoList> {
      return json(
        `/v1/scheduling/appointments/${uuid(appointmentId)}/health-card-photos`,
        HealthCardPhotoListSchema,
        "The health card photos could not be loaded.",
        undefined,
        signal,
      );
    },

    /**
     * Sends a staff photo of the card for the chart: the browser's JPEG with its metadata already
     * stripped (prepareHealthCardPhoto). It replaces the chart's earlier staff photo once checked.
     * Bytes the server would refuse are refused here first, so nothing leaves.
     */
    async uploadPatientHealthCardPhoto(
      patientId: string,
      jpeg: Uint8Array,
      signal?: AbortSignal,
    ): Promise<HealthCardPhoto> {
      const path = `/v1/patients/${uuid(patientId)}/health-card-photos`;
      if (!inspectHealthCardJpeg(jpeg).ok) {
        throw new SchedulingRequestError("This photo can't be sent. Take it again.", {
          status: 415,
          detail: "health_card_photo_invalid",
        });
      }
      const response = await send(
        path,
        "The health card photo could not be saved.",
        {
          method: "POST",
          headers: { "content-type": HEALTH_CARD_PHOTO_MEDIA_TYPE },
          body: new Blob([jpeg.slice()], { type: HEALTH_CARD_PHOTO_MEDIA_TYPE }),
        },
        signal,
      );
      return HealthCardPhotoSchema.parse(await response.json());
    },

    /**
     * The photo's bytes (an audited read): only a clean photo opens; one still being checked or
     * blocked is refused with `health_card_photo_not_ready`. Anything but a JPEG is refused here.
     */
    async openHealthCardPhoto(photoId: string, signal?: AbortSignal): Promise<Blob> {
      const response = await send(
        `/v1/scheduling/health-card-photos/${uuid(photoId)}/content`,
        "The health card photo could not be opened.",
        { method: "GET" },
        signal,
      );
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (!inspectHealthCardJpeg(bytes).ok) throw new Error("The photo could not be shown.");
      return new Blob([bytes], { type: HEALTH_CARD_PHOTO_MEDIA_TYPE });
    },

    async deleteHealthCardPhoto(
      photoId: string,
      signal?: AbortSignal,
    ): Promise<HealthCardPhotoDeleteReceipt> {
      return json(
        `/v1/scheduling/health-card-photos/${uuid(photoId)}/delete`,
        HealthCardPhotoDeleteReceiptSchema,
        "The health card photo could not be deleted.",
        {},
        signal,
      );
    },
  };
}

export type HealthCardPhotosClient = ReturnType<typeof createHealthCardPhotosClient>;

/** Reads the settings for capture availability: the registration forms' photo field. */
export type HealthCardPhotoCaptureClient = Pick<
  HealthCardPhotosClient,
  "getHealthCardPhotoSettings"
>;

/** The settings read in the query cache (not audited). */
export const healthCardPhotoSettingsKey = (siteId: string) =>
  ["health-card-photo-settings", siteId] as const;

/** A chart's photo list in the query cache (metadata only, not audited). */
export const patientHealthCardPhotosKey = (siteId: string, patientId: string) =>
  ["health-card-photos", siteId, "patient", patientId] as const;

/** A booking's photo list in the query cache (metadata only, not audited). */
export const appointmentHealthCardPhotosKey = (siteId: string, appointmentId: string) =>
  ["health-card-photos", siteId, "appointment", appointmentId] as const;

/** The route is not there (an older agent-api) or the role may not use it: show nothing. */
export function isHealthCardPhotosHidden(error: unknown): boolean {
  return (
    error instanceof SchedulingRequestError &&
    (error.status === 404 || error.status === 401 || error.status === 403)
  );
}

/** No photo checker is running: photos can't be sent now. */
export function isHealthCardPhotosUnavailable(error: unknown): boolean {
  return (
    error instanceof SchedulingRequestError && error.detail === "health_card_photos_unavailable"
  );
}

/** Someone else saved the clinic's photo settings first. */
export function isStaleHealthCardPhotoSettings(error: unknown): boolean {
  return (
    error instanceof SchedulingRequestError && error.detail === "health_card_photo_settings_changed"
  );
}

/** Words for a refused staff photo, shown on the photo's row. */
export function healthCardPhotoUploadRefusal(error: unknown): string {
  if (isHealthCardPhotosUnavailable(error)) {
    return "Photos can't be saved right now: the photo check isn't running. Type the card details.";
  }
  if (error instanceof SchedulingRequestError && error.detail === "health_card_photo_invalid") {
    return "This photo can't be used. Take it again.";
  }
  if (error instanceof SchedulingRequestError && error.status === 403) {
    return "Your role can't add a health card photo.";
  }
  return "The photo was not saved. Try again.";
}
